import { useLocation, useNavigate } from 'react-router-dom';
import styles from './EmployeeProfileModeSwitch.module.css';

/**
 * Snapshot / Detailed — the one control that moves between the two
 * (Mission 171, "Snapshot + Detailed Profile Experience").
 *
 * ---------------------------------------------------------------------
 * REAL NAVIGATION, NEVER A TRAPPED TAB
 *
 * Both destinations are real paths (`/people/:id`, `/people/:id/detailed`),
 * and a real `<a href>` is what's in the DOM — so a reader opening either
 * in a new tab, copying the link, or simply reading the status bar sees an
 * honest URL. The click itself is intercepted to call `useNavigate()`
 * directly, rather than rendering `<Link>`/`NavLink`: this page's own
 * automated test suite (not real-browser QA, where no equivalent issue was
 * ever observed) intermittently hangs — an unbounded, non-CPU-bound
 * render/effect stall reproducible in this specific jsdom/`MemoryRouter`
 * test harness — when its tests render this page's full tree repeatedly in
 * one process. Extensive bisection narrowed it only this far: it is not
 * tied to any one data combination, not explained by system memory
 * pressure (reproduced on a freshly rebooted, fully idle machine), and
 * `<Link>`/`NavLink` were the most-implicated component in the
 * investigation, so `useNavigate()` is kept as a reasonable, low-cost,
 * behaviourally-identical precaution — not a confirmed fix, since the
 * failure was also observed to recur after switching to it. See the
 * Mission 171 final report for the complete investigation record, and
 * `docs/FRONTEND.md` §5w for the summary.
 *
 * `useLocation().pathname` decides which destination reads as current —
 * the exact same derivation `EmployeeProfilePage.tsx` itself already uses
 * to pick a mode, so the two can never disagree about which mode is
 * showing.
 * ---------------------------------------------------------------------
 */
export function EmployeeProfileModeSwitch({ employeeId }: { employeeId: string }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isDetailed = pathname.endsWith('/detailed');

  function go(event: React.MouseEvent<HTMLAnchorElement>, to: string) {
    // The usual "let the browser handle it" exceptions — a modified click
    // (open in new tab/window, etc.) must still work normally.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  }

  return (
    <nav className={styles.switch} aria-label="Profile view">
      <a
        href={`/people/${employeeId}`}
        onClick={(event) => go(event, `/people/${employeeId}`)}
        aria-current={isDetailed ? undefined : 'page'}
        className={isDetailed ? styles.item : styles.itemActive}
      >
        Snapshot
      </a>
      <a
        href={`/people/${employeeId}/detailed`}
        onClick={(event) => go(event, `/people/${employeeId}/detailed`)}
        aria-current={isDetailed ? 'page' : undefined}
        className={isDetailed ? styles.itemActive : styles.item}
      >
        Detailed
      </a>
    </nav>
  );
}
